/**
 * Source-of-truth message catalogue. Every other locale is checked against
 * these keys at build time by `npm run i18n:check`.
 *
 * House style: no sentence is assembled from fragments, and no number is
 * interpolated without going through ICU. "3 cards left" is a plural rule in
 * six of our launch locales and a different one in Arabic.
 */
export const en = {
  'app.name': 'Rummy',
  'app.install': 'Install',
  'app.tagline': 'A table for your people. Nothing staked, ever.',

  'auth.heading': 'Pull up a seat',
  'auth.google': 'Continue with Google',
  'auth.apple': 'Continue with Apple',
  'auth.email': 'Email me a link',
  'auth.guest': 'Play as a guest',
  'auth.retry': 'Try again',
  'auth.guest.connecting': 'Connecting…',
  'auth.or': 'or',
  'auth.email.label': 'Email',
  'auth.password.label': 'Password',
  'auth.signin': 'Sign in',
  'auth.signup': 'Create account',
  'auth.haveAccount': 'Already a member?',
  'auth.newHere': 'New here?',
  'auth.profile.heading': 'Make it yours',
  'auth.shuffle': 'Shuffle',
  'auth.enter': 'Enter the club',
  'auth.error.email-taken': 'That email already has an account. Sign in instead.',
  'auth.error.invalid-credentials': 'Wrong email or password.',
  'auth.error.invalid-email': 'That does not look like an email address.',
  'auth.error.weak-password': 'Use at least 8 characters.',
  'auth.error.rate-limited': 'Too many attempts. Wait a few minutes.',
  'auth.error.network': 'Could not reach the server. Try again.',
  'auth.name.label': 'What should the table call you?',
  'auth.name.placeholder': 'Your name',
  'auth.pickCharacter': 'Pick a face',
  'auth.adult': 'I am 18 or over',
  'auth.adult.note':
    'This table is for adults. No deposits, no withdrawals, no winnings — points reset every session.',
  'auth.demoNote':
    'Guest identity is a signed token on this device. Create an account to keep it.',

  'lobby.heading': 'Lobby',
  'lobby.hey': 'Hey',
  'lobby.sub': 'Who are we playing tonight?',
  'lobby.create.note': 'Open a table and pull in your people.',
  'lobby.practice': 'Practice',
  'lobby.practice.note': 'A full table of bots, ready in one tap.',
  'lobby.practice.difficulty': 'Difficulty',
  'lobby.practice.difficulty.casual': 'Casual',
  'lobby.practice.difficulty.sharp': 'Sharp',
  'lobby.practice.difficulty.expert': 'Expert',
  'lobby.joinByCode': 'Have a code?',
  'lobby.codePlaceholder': 'amber-koel-42',
  'lobby.friends': 'Friends',
  'lobby.friends.empty': 'Play a table and the people at it show up here.',
  'lobby.friends.explain': 'You become friends by playing together. There is no search.',
  'lobby.rooms': 'Recent rooms',
  'lobby.rooms.empty': 'Nothing yet.',
  'lobby.result.youWon':
    'You won the last one, over {hands, plural, one {# hand} other {# hands}}.',
  'lobby.result.youPlaced': '{winner} won the last one · you finished {place} of {players}.',
  'lobby.result.won': '{winner} won the last one.',
  'lobby.create': 'Create room',
  'lobby.join': 'Join',
  'lobby.watch': 'Watch',
  'lobby.invite': 'Invite',
  'lobby.seatCount':
    '{seated, plural, one {# seat filled} other {# seats filled}} of {total, number}',

  'presence.online': 'Online',
  'presence.at-table': 'At {room}',
  'presence.away': 'Away',
  'presence.offline': 'Offline',

  'room.heading': 'Room {id}',
  'room.format': 'Format',
  'room.seats': 'Seats',
  'room.turnTimer': 'Turn timer',
  'room.turnTimer.value': '{seconds, number} seconds',
  'room.turnTimer.short': '{seconds, number}s',
  'room.guard': 'Declaration guard',
  'room.guard.help':
    'Blocks a declaration that has no pure sequence. Removes the worst mistake, and some of the skill. Off by default; the whole table sees this setting.',
  'room.guard.on': 'On',
  'room.guard.off': 'Off',
  'room.hostControls': 'Host controls',
  'room.seatLock': 'Lock new seats',
  'room.pauseBetweenHands': 'Host starts each next hand',
  'room.reentry': 'Re-entry',
  'room.reentry.on': 'One buy-back allowed',
  'room.reentry.off': 'Straight elimination',
  'room.reentry.help':
    'A player who crosses 201 may come back once, resuming at the highest score still in play — so their total can go down. Turn this off and 201 is the end of their session.',
  'room.startCountdown': 'Start in 15 seconds',
  'room.invite': 'Invite link',
  'room.copy': 'Copy',
  'room.copied': 'Copied',
  'room.addBot': 'Add practice opponent',
  'room.start': 'Start session',
  'room.start.hint': 'Two seats needed to start.',
  'room.leave': 'Leave',
  'room.remove': 'Remove',
  'room.host': 'Host',
  'room.queued': 'Next session',
  'room.queued.note': 'Seats lock when the session starts. These players are in for the next one.',
  'room.startsAt': 'Starts {at, time, short} on {at, date, medium}',
  'room.you': 'You',
  'room.decks':
    'Two seats play one deck. Three to six play two, the way the game is normally dealt. Seven or more play three, so the stock stays deep enough to draw from.',
  'room.decks.short': '{decks, plural, one {One deck} other {# decks}}',
  'room.eyebrow': 'Your table',
  'room.crest': 'Table crest',
  'room.crest.spade': 'Spade crest',
  'room.crest.lotus': 'Lotus crest',
  'room.crest.sun': 'Sun crest',
  'room.crest.moon': 'Moon crest',
  'room.untitled': 'Untitled table',
  'room.rename': 'Rename this table',
  'room.namePlaceholder': 'Thursday night',
  'room.fillSeat': 'Fill this seat',
  'room.seatMenu.invite': 'Copy invite link',
  'room.closeSeat': 'Close this seat',
  'room.fillTable': 'Fill the table',
  'room.fillTable.note':
    '{count, plural, one {Seats a practice opponent in the open chair} other {Seats practice opponents in the # open chairs}}',
  'room.autoFill': 'Fill empty seats at start',
  'room.autoFill.help':
    'When the session starts, any chair still empty gets a practice opponent, so a table set for four starts as a four-hander. A player who leaves mid-session is never replaced.',
  'room.fillSkill': 'Opponent strength',
  'room.seat.style': 'Play style',
  'room.seat.styleFor': 'Play style for {name}',
  'room.seats.add': 'Add a seat',
  'room.seats.remove': 'Remove a seat',
  'room.seats.count': '{seats, plural, one {# seat} other {# seats}}',
  'room.qr': 'QR',
  'room.qr.hide': 'Hide QR',
  'room.qr.alt': 'Scan to join this table',
  'room.qr.note': 'Point a phone camera at this to take a seat.',
  'room.rules': 'Table rules',
  'room.rules.summary':
    '{seats, number} seats · {seconds, number}s turns · guard {guard, select, on {on} other {off}}',
  'room.primer.open': 'How a hand works',
  'room.primer.eyebrow': 'A 30-second tour',
  'room.primer.draw.title': 'Draw one card',
  'room.primer.draw.copy': 'On your turn, choose the closed deck or take the visible card from the open pile.',
  'room.primer.build.title': 'Build clean groups',
  'room.primer.build.copy': 'Make sequences and sets. A valid declaration needs at least two sequences, including one pure sequence.',
  'room.primer.finish.title': 'Discard, then declare',
  'room.primer.finish.copy': 'Discard one card to end a turn. When every card is grouped validly, declare to finish the hand.',
  'room.primer.back': 'Back',
  'room.primer.next': 'Next',
  'room.primer.done': 'Got it',
  'room.primer.close': 'Close',
  'room.playNow': 'Play now',
  'room.playLater': 'Schedule',
  'format.family.deals': 'Deals',
  'format.family.pool': 'Pool',
  'room.atTheTable': 'At the table',
  'room.emptySeat': 'Open seat',
  'room.chooseOpponent': 'Who are you playing against?',
  'room.invite.note': 'Anyone with this link can take a seat. No account needed.',

  'invite.preview.eyebrow': 'You are invited to a table',
  'invite.preview.host': 'Hosted by {name}',
  'invite.preview.live': 'Live now',
  'invite.preview.waiting': 'Getting ready',
  'invite.preview.live.copy': 'Watch the current hand without taking a seat. Your friends’ cards stay private.',
  'invite.preview.waiting.copy': 'The table is waiting for players. Take a seat when you are ready.',
  'invite.preview.full.copy':
    'Every chair at this table is taken. Join and you will be seated for the next session.',
  'invite.preview.watch': 'Watch live',
  'invite.preview.joinNext': 'Join next hand',
  'invite.preview.join': 'Join table',
  'invite.preview.cancel': 'Maybe later',
  'room.schedule': 'Start time',
  'room.ready': 'Ready when you are.',
  'room.bot': 'Practice',
  'bot.trait.nila': 'Patient. Waits for the sequence rather than forcing it.',
  'bot.trait.arun': 'Expert tactician. Uses open-pile signals and protects his outs.',
  'bot.trait.devi': 'Careful. Holds jokers back for the second sequence.',
  'bot.trait.kabir': 'Blunt. Throws the expensive cards early and does not look back.',
  'bot.trait.meera': 'Expert closer. Calculates every route to a clean declaration.',
  'bot.trait.ravi': 'Opportunist. Takes the open card whenever it helps at all.',
  'bot.trait.sana': 'Sharp. Plays the percentages and drops a hopeless hand.',
  'bot.trait.jai': 'Easy-going. Never drops, never rushes, good to learn against.',
  'bot.trait.ila': 'Tidy. Will not throw a card that fits anywhere.',
  'bot.trait.tara': 'Sharp. Quick to read the pile and quicker to close.',
  'bot.style.nila': 'Patient builder',
  'bot.style.arun': 'Expert tactician',
  'bot.style.devi': 'Joker keeper',
  'bot.style.kabir': 'Low-card cutter',
  'bot.style.meera': 'Expert closer',
  'bot.style.ravi': 'Open-pile opportunist',
  'bot.style.sana': 'Percentage player',
  'bot.style.jai': 'Easy-going',
  'bot.style.ila': 'Tidy builder',
  'bot.style.tara': 'Quick closer',

  'bot.skill.casual': 'Casual',
  'bot.skill.sharp': 'Sharp',
  'bot.skill.expert': 'Expert',
  'bot.skill.patient': 'Patient',
  'bot.skill.opportunist': 'Opportunist',
  'bot.skill.keeper': 'Joker keeper',
  'bot.skill.low-card': 'Low-card cutter',
  'bot.skill.tidy': 'Tidy',

  'format.deals-2': 'Deals · 2 hands',
  'format.deals-3': 'Deals · 3 hands',
  'format.deals-6': 'Deals · 6 hands',
  'format.pool-101': 'Pool 101',
  'format.pool-201': 'Pool 201',
  'format.deals.explain': 'A fixed number of hands. Lowest total wins.',
  'format.pool.explain': 'Cross the threshold and you are out. Last player standing wins.',
  'format.note.deals-2': 'Two hands. Quick.',
  'format.note.deals-3': 'Three hands. Lowest total wins.',
  'format.note.deals-6': 'Six hands. A long evening.',
  'format.note.pool-101': 'Out at 101. Usually the shorter pool.',
  'format.note.pool-201': 'Out at 201, with one re-entry.',

  'table.yourTurn': 'Your turn',
  'table.notify.yourTurn.title': "It's your turn",
  'table.turnOf': '{name} is thinking',
  'table.turnGuide.draw': 'Draw one card from either pile.',
  'table.turnGuide.discard': 'Choose one card to discard.',
  'table.turnGuide.discardSelected': 'Your card is selected — discard it when ready.',
  'table.handProgress': 'Hand progress',
  'table.discard': 'Discard',
  'table.discard.here': 'Drop here',
  'table.declare': 'Declare',
  'table.drop': 'Drop',
  'table.drop.first': 'Drop for {points, number}',
  'table.sort': 'Sort',
  'table.undo': 'Undo sort',
  'table.closed': 'Closed deck',
  'table.closed.count': '{count, plural, one {# card} other {# cards}} left',
  'table.open': 'Open pile',
  'table.open.empty': 'Empty',
  'table.wild': 'Wild joker',
  'table.wild.short': '{rank} is wild',
  'table.wild.explain':
    '{decks, plural, one {Every {rank} is wild this hand.} other {Every {rank} is wild this hand, in both decks.}}',
  'table.points': 'If the hand ends now: {points, number}',
  'table.yourScore': 'Your total',
  'table.pointsReady': 'A valid hand. You can declare.',
  'table.handOf': 'Hand {n, number}',
  'table.handOfTotal': 'Hand {n, number} of {total, number}',
  'table.reconnecting': 'Reconnecting',
  'table.autoplay.warning': '{name} has missed {count, plural, one {# turn} other {# turns}}',
  'table.deckCommit': 'Deck committed',
  'table.leave': 'Leave table',
  'table.emote': 'React',
  'table.highlights': 'Highlights',
  'table.history': 'Past hands',
  'table.intel': 'Table intel',
  'table.more': 'More',
  'history.heading': 'Past hands',
  'history.close': 'Close',
  'history.empty': 'No hands finished yet.',
  'history.hand': 'Hand {n}',
  'history.wonBy': 'Won by {name}',
  'history.noWinner': 'No declaration',
  'table.intel.ledger': 'Seen cards',
  'table.intel.moves': 'Public moves',
  'table.intel.empty': 'Public cards will collect here as the hand develops.',
  'table.intel.open': 'took open',
  'table.intel.closed': 'drew closed',
  'table.intel.threw': 'threw {card}',
  'table.recovered': 'Back at the table · {count, plural, one {# public move while you were away} other {# public moves while you were away}}',
  'table.shortcuts': 'Shortcuts',
  'table.shortcuts.copy': 'D closed draw · O open draw · X discard selected · G new group · C declare · ? shortcuts',
  'table.highlights.empty': 'The next move will appear here.',
  'table.someone': 'Someone',
  'table.watching': 'Watching live. Cards stay private until they are shown.',
  'table.highlight.dealt': 'Cards are dealt.',
  'table.highlight.drewOpen': '{name} took the open card.',
  'table.highlight.drewClosed': '{name} drew from the closed pile.',
  'table.highlight.discarded': '{name} discarded.',
  'table.highlight.dropped': '{name} dropped from this hand.',
  'table.highlight.declared': '{name} declared.',
  'table.highlight.misdeclared': '{name} made an invalid declaration.',
  'table.highlight.recycled': 'The discard pile was recycled.',
  'table.highlight.completed': 'This hand is complete.',
  'table.recovery.heading': 'Reconnecting to your table',
  'table.recovery.copy': 'Your last confirmed table state is safe. New moves wait for the connection.',
  'table.recovery.copy.timed':
    'Your seat is held for {seconds}s. Your last confirmed table state is safe until then.',
  'table.recovery.retry': 'Retry now',
  'table.network.slow': 'Network may be slow',
  'table.network.copy': 'Animations are kept light while your connection catches up.',
  'table.discardCoach.good': 'This discard keeps your best hand intact.',
  'table.discardCoach.better': 'Another discard could save {points, number} points.',
  'table.recap': 'Your last turn: drew from {draw}, then discarded.',
  'table.recap.open': 'the open pile',
  'table.recap.closed': 'the closed pile',
  'table.fullscreen': 'Focus table',
  'table.fullscreen.exit': 'Exit focus',
  'table.assist.run': 'A run is taking shape here.',
  'table.assist.set': 'This pair is one card from a set.',
  'table.reaction.nice': 'Nice',
  'table.reaction.run': 'Nice run',
  'table.reaction.close': 'So close',
  'table.reaction.wild': 'Wild!',
  'table.spectating': 'Watching. You are seated for the next session.',
  'table.eliminated': 'Out',
  'seat.cards': '{count, plural, one {# card} other {# cards}}',
  'points.short': '{points, number} pts',
  'table.dropped': 'Dropped',
  'table.order.next': 'Next',
  'table.order.away': '+{count, number}',
  'table.autoDropped': 'Timed out',
  'table.youDropped': 'You dropped this hand — {points, number} points',
  'table.waitingForHost': 'Waiting for the host to start.',
  'table.yourTurn.timed': 'Your turn · {seconds, number}s',
  'table.missWarning':
    'Missed {count, plural, one {# turn} other {# turns}}. Miss one more and you are dropped for {points, number}.',
  'table.timedOut': 'Your turn ran out. The minimum move was played for you.',

  'log.drew.closed': '{name} drew from the closed deck',
  'log.drew.discard': '{name} took the open card',
  'log.discarded': '{name} discarded',
  'log.dropped': '{name} dropped',
  'log.declared.valid': '{name} declared',
  'log.declared.invalid': '{name} declared and it did not stand',
  'log.recycled': 'The open pile was shuffled back into the deck',
  'log.autoplay': '{name} timed out and played the minimum move',

  'group.pure': 'Pure sequence',
  'group.impure': 'Sequence',
  'group.set': 'Set',
  'group.incomplete': 'Not a combination yet',
  'group.new': 'New group',
  'group.selected': '{count, plural, one {# card selected} other {# cards selected}}',

  'declare.heading': 'Declare',
  'declare.explain': 'This is exactly what the server will see.',
  'declare.finish': 'Finish card',
  'declare.finish.pick': 'Choose the card to place face down.',
  'declare.finish.selected': 'Use selected card',
  'declare.finish.change': 'Choose another card',
  'declare.drawFirst': 'Draw one card first, then choose your finish card.',
  'declare.waitTurn': 'Declare when it is your turn after you draw.',
  'declare.confirm': 'Confirm declaration',
  'declare.cancel': 'Go back',
  'declare.invalid': 'This grouping is not a valid hand. Declaring anyway costs 80.',
  'declare.valid': 'Two sequences, one of them pure. This stands.',
  'declare.guardBlocked':
    'The room has the declaration guard on and this hand has no pure sequence.',

  'show.heading': 'Show',
  'show.explain': 'Arrange your hand before it is scored. {seconds, number} seconds.',
  'show.submit': 'Submit arrangement',
  'show.winner': '{name} declared',
  'show.waiting': 'Waiting for {names}',
  'show.submitted': 'Arrangement in.',
  'show.arranging': 'Arranging',
  'show.yourTurnToArrange': 'Arrange your hand, then submit. Scoring starts as soon as everyone is in.',

  'summary.heading': 'Hand {n, number}',
  'summary.points': 'Points',
  'summary.total': 'Total',
  'summary.reentry': 'Crossed 201 on {from, number} · back in at {to, number}',
  'summary.best': 'Best possible was {points, number}',
  'summary.next': 'Next hand in {seconds, number}',
  'summary.continue': 'Continue',
  'summary.continue.in': 'Continue · {seconds, number}s',
  'summary.replay': 'Replay finish',
  'summary.replay.close': 'Close replay',
  'summary.replay.title': '{name}’s finish',
  'summary.replay.copy': 'The final arrangement, dealt back one card at a time.',
  'summary.review': 'Review your turns',
  'summary.review.close': 'Close review',
  'summary.review.title': 'Your decision trace',
  'summary.review.copy': 'A private record of the information you saw and the cards you released.',
  'summary.review.closed': 'Closed draw',
  'summary.review.open': 'Open draw {card}',
  'summary.review.discard': 'Discarded {card}',
  'summary.expert.instead':
    'You threw {yours}; the expert throws {theirs} — {points, plural, one {# point} other {# points}} cheaper.',
  'summary.expert.missedDeclaration':
    'This hand was already won — throwing {card} here would have declared it.',
  'summary.coach.heading': 'Hand coach',
  'summary.coach.grouping': 'A different grouping would have saved {points, number} points.',
  'summary.coach.pure': 'A pure sequence was available in this hand.',
  'summary.coach.deadwood': '{count, plural, one {# card still had nowhere safe to go.} other {# cards still had nowhere safe to go.}}',
  'summary.coach.show': 'Show better grouping',
  'summary.coach.hide': 'Hide grouping',
  'outcome.won': 'Won',
  'outcome.lost': 'Lost',
  'outcome.first-drop': 'First drop',
  'outcome.middle-drop': 'Drop',
  'outcome.auto-drop': 'Timed out',
  'outcome.invalid-declaration': 'Invalid declaration',

  'session.heading': 'Session complete',
  'session.winner': '{name} wins',
  'session.winner.tie': 'Tied',
  'session.rematch': 'Rematch',
  'session.leave': 'Leave',
  'session.share': 'Share recap',
  'session.shared': 'Recap shared',
  'session.details.show': 'View session details',
  'session.details.hide': 'Close session details',
  'session.byHand': 'Hand by hand',
  'session.hands': '{n, plural, one {# hand} other {# hands}}',
  'session.hand': 'Hand',
  'session.margin': 'Won by {points, number} points',
  'session.margin.none': 'Level at the top',
  'session.celebrate': 'Celebrate again',
  'session.you': 'You',
  'session.out': 'Out',
  'session.outAt': 'Out at hand {n, number}',
  'session.reentry': 'Re-entered at {to, number} (hand {n, number})',
  'session.reentry.note':
    'A re-entry resets a score to the highest still in play, so a column of hands can add up to more than the total beneath it.',
  'session.tab.standings': 'Standings',
  'session.tab.progress': 'Progress',
  'session.tab.hands': 'Hands',
  'session.stat.won': 'Hands won',
  'session.stat.average': 'Avg per hand',
  'session.stat.best': 'Best hand',
  'session.stat.drops': 'Drops',
  'session.award.wins': 'Most hands won',
  'session.award.steady': 'Steadiest',
  'session.award.tight': 'Closest finish',
  'session.award.wins.value': '{n, plural, one {# hand} other {# hands}}',
  'session.tiltNudge':
    '{n} sessions without a win. No rush — Practice mode has no stakes and lets you set the pace.',
  'session.award.points.value': '{points, number} pts',
  'session.chart.hand': 'After hand {n, number}',
  'session.chart.start': 'Before the first hand',
  'session.chart.empty': 'No hands were played.',
  'session.chart.a11y':
    'Score after each hand. Use the arrow keys to step through the session; the figures below update as you go.',

  'personal.eyebrow': 'Your form',
  'personal.heading': 'Personal stats studio',
  'personal.device': 'This device',
  'personal.account': 'Your account',
  'personal.sessions': 'Sessions',
  'personal.winRate': 'Hand win rate',
  'personal.avgPoints': 'Avg points',
  'personal.drops': 'Drops',

  'achievement.eyebrow': 'Just for you',
  'achievement.heading': 'Table milestones',
  'achievement.first-win.title': 'First hand won',
  'achievement.first-win.copy': 'Your first recorded hand win.',
  'achievement.five-wins.title': 'Five hands won',
  'achievement.five-wins.copy': 'A quiet marker of real table time.',
  'achievement.steady-player.title': 'Steady player',
  'achievement.steady-player.copy': 'Three completed sessions.',
  'achievement.calm-table.title': 'Calm table',
  'achievement.calm-table.copy': 'A three-hand session without a drop.',
  'achievement.next':
    '{remaining, plural, one {One more} other {# more}} from “{title}”.',

  'verify.heading': 'Deck verification',
  'verify.explain':
    'The deck order was committed before the deal and revealed after. This proves the order was fixed in advance and not changed mid-hand. It does not prove the shuffle was uniformly random.',
  'verify.check': 'Verify this hand',
  'verify.copy': 'Copy receipt',
  'verify.copied': 'Receipt copied',
  'verify.ok': 'The revealed deck matches the commitment.',
  'verify.fail': 'The revealed deck does not match. Please report this.',
  'verify.commit': 'Commitment',

  'settings.heading': 'Settings',
  'settings.section.play': 'Play',
  'settings.section.look': 'Look',
  'settings.section.access': 'Reading the table',
  'settings.experience.learning': 'Learning',
  'settings.experience.regular': 'Regular',
  'settings.experience.expert': 'Expert',
  'settings.experience.custom': 'Your own mix',
  'settings.experience.help':
    'Sets how much the table explains itself. Adjust anything below and this becomes your own mix.',
  'settings.theme': 'Theme',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme.system': 'System',
  'settings.deck': 'Deck colours',
  'settings.deck.two': 'Two colour',
  'settings.deck.four': 'Four colour',
  'settings.tableSkin': 'Table mood',
  'settings.tableSkin.jade': 'Jade lounge',
  'settings.tableSkin.midnight': 'Midnight violet',
  'settings.tableSkin.noir': 'Noir marble',
  'settings.tableSkin.sunset': 'Sunset club',
  'settings.tableSkin.seasonal': 'Seasonal club',
  'settings.cardBack': 'Signature card back',
  'settings.cardBack.club': 'Club violet',
  'settings.cardBack.aurora': 'Aurora',
  'settings.cardBack.ruby': 'Ruby',
  'settings.cardBack.ivory': 'Ivory',
  'settings.playerAccent': 'Player accent',
  'settings.playerAccent.violet': 'Violet',
  'settings.playerAccent.rose': 'Rose',
  'settings.playerAccent.aqua': 'Aqua',
  'settings.playerAccent.gold': 'Gold',
  'settings.accessibility': 'Comfort & accessibility',
  'settings.cardSize.compact': 'Compact cards',
  'settings.cardSize.comfortable': 'Comfortable cards',
  'settings.cardSize.large': 'Large cards',
  'settings.handedness.right': 'Right-handed layout',
  'settings.handedness.left': 'Left-handed layout',
  'settings.highContrast': 'High contrast',
  'settings.motion': 'Motion',
  'settings.motion.full': 'Full',
  'settings.motion.reduced': 'Reduced',
  'settings.motion.none': 'None',
  'settings.motion.help':
    'Reduced keeps the movement that tells you something — a card changing hands, a group closing, your turn arriving — and drops the atmosphere. Your device setting still wins if it asks for less.',
  'settings.turnAlert': 'Chime when it’s my turn',
  'settings.sound': 'Card sounds',
  'settings.turnNotifications': 'Notify me when it’s my turn (backgrounded tab)',
  'settings.confirmDiscard': 'Confirm before discarding',
  'settings.quickPlay': 'Quick play — auto-sort after drawing and reduce table effects',
  'settings.expertMode': 'Expert table — dense controls and hand status',
  'settings.tableIntel': 'Table intel — track seen cards and public moves',
  'settings.gameplayGuidance': 'Show meld progress and gameplay suggestions',
  'settings.language': 'Language',
  'settings.close': 'Close',
  'settings.signout': 'Sign out',
  'settings.guest': 'Guest — nothing saved beyond this device',


  'a11y.hand': 'Your hand, {count, plural, one {# card} other {# cards}}',
  'a11y.card': '{rank} of {suit}',
  'a11y.joker': 'Joker',
  'a11y.group': 'Group {n, number}, {kind}',
  'a11y.keyboard':
    'Arrow keys move along the tray. Space picks a card up, Enter places it. D draws from the closed deck, Shift D takes the open card. S sorts.',
  'a11y.turnTimer': '{seconds, number} seconds left in this turn',

  'error.connecting': 'Connecting…',
  'error.connection': 'Lost the connection. Trying again.',
  'error.offline': 'Cannot reach the table. Still trying.',
  'error.connected': 'Back on.',
  'error.rate-limited': 'Slow down a moment.',
  'error.not-your-turn': 'Not your turn.',
  'error.must-draw': 'Draw a card first.',
  'error.must-discard': 'Discard before doing that.',
  'error.card-not-held': 'That card is not in your hand.',
  'error.no-room': 'That room is not there any more.',
  'error.stale-state': 'The table changed. Your view has been refreshed.',
  'error.bad-action': 'That move could not be understood.',
  'error.guard-no-pure-sequence': 'The declaration guard stopped this: no pure sequence.',
  'error.generic': 'Something went wrong.',

  'card.index.A': 'A',
  'card.index.J': 'J',
  'card.index.Q': 'Q',

  'rules.back': '\u2039 Back',
  'rules.heading': 'How to play',
  'rules.lede':
    'Indian Rummy, thirteen cards. If you have never played, everything you need is on this page — it takes about four minutes to read and one hand to understand.',

  'rules.goal.title': 'What you are trying to do',
  'rules.goal.body':
    'You hold thirteen cards. You are trying to arrange all thirteen into valid combinations — runs of consecutive cards in one suit, or groups of the same number in different suits. The first player to get all thirteen arranged and say so wins the hand. Everybody else scores points for the cards they were still holding, and in Rummy points are bad: the lowest total wins.',

  'rules.table.title': 'The table',
  'rules.table.body':
    'Two decks are shuffled together, so every card exists twice. In the middle are two piles: the closed deck, face down, and the open pile, face up. You can take the top card of either.',
  'rules.table.wild':
    'One card is turned up at the start to pick the wild joker. Whatever its number is, all four suits of that number become wild for the hand, and so do the printed jokers. A wild can stand in for any card you are missing, and it costs you nothing if you are caught holding it.',

  'rules.turn.title': 'Your turn',
  'rules.turn.draw': 'Take one card — from the closed deck, or the face-up card on the open pile.',
  'rules.turn.arrange': 'Drag your cards into groups. You can rearrange as much as you like, on your turn or off it.',
  'rules.turn.discard': 'Throw one card away, face up, onto the open pile. Now you are back to thirteen and the turn moves on.',
  'rules.turn.note':
    'One card in, one card out, every turn. You cannot take a card off the open pile and throw the same card straight back.',

  'rules.combos.title': 'What counts as a combination',

  'rules.pure.title': 'Pure sequence',
  'rules.pure.body':
    'Three or more cards in a row, all the same suit, with no joker helping. Ace counts either low (A-2-3) or high (Q-K-A). You must have at least one of these, and it is the single most important thing on this page.',
  'rules.pure.yes': 'Three hearts in a row, no joker — a pure sequence.',
  'rules.pure.no': 'Right numbers, mixed suits. Not a sequence at all.',
  'rules.pure.corner': 'You cannot turn the corner: K-A-2 is not a run.',

  'rules.impure.title': 'Impure sequence',
  'rules.impure.body':
    'The same thing, but a wild fills a gap. It is a real sequence and counts towards your two — it just is not the pure one.',
  'rules.impure.yes': 'The joker stands in for the 6 of hearts.',

  'rules.set.title': 'Set',
  'rules.set.body':
    'Three or four cards of the same number, every one a different suit. A wild can replace any of them. Because there are two decks, watch for the trap below.',
  'rules.set.yes': 'Three nines, three different suits — a set.',
  'rules.set.no': 'Two nines of spades. Same suit twice is never a set.',

  'rules.declare.title': 'Finishing the hand',
  'rules.declare.body':
    'When your fourteenth card would leave a complete thirteen behind, you throw that card and declare. To be valid your arrangement must satisfy all three of these:',
  'rules.declare.r1': 'Every one of the thirteen cards is in a combination — nothing left over.',
  'rules.declare.r2': 'At least two of those combinations are sequences.',
  'rules.declare.r3': 'At least one of those sequences is pure — no joker in it.',
  'rules.declare.warn':
    'Declaring without those is the most expensive mistake in the game: it costs a flat 80 and you are out of the hand. Count twice before you declare.',

  'rules.score.title': 'Scoring',
  'rules.score.body':
    'The winner scores nothing. Everybody else adds up the cards that are not protected by a valid combination.',
  'rules.score.win': 'Winning the hand',
  'rules.score.win.v': '0',
  'rules.score.card': 'Number cards',
  'rules.score.card.v': 'Face value — a 7 is 7 points',
  'rules.score.wild': 'Ace, King, Queen, Jack',
  'rules.score.wild.v': '10 each. Jokers and wilds cost nothing.',
  'rules.score.cap': 'Most you can lose in one hand',
  'rules.score.cap.v': '80',
  'rules.score.first': 'Dropping before your first turn',
  'rules.score.first.v': '20',
  'rules.score.middle': 'Dropping later, or being timed out',
  'rules.score.middle.v': '40',
  'rules.score.invalid': 'Declaring when you were not ready',
  'rules.score.invalid.v': '80',
  'rules.score.protect':
    'What gets protected depends on how far you got. With no pure sequence, nothing is protected and every card counts against you. With a pure sequence but only one sequence, only the cards inside that pure run are safe. Once you have two sequences including a pure one, every valid combination counts and only your loose cards score. This is why the pure sequence matters more than anything else.',

  'rules.formats.title': 'Formats',
  'rules.formats.deals':
    'Deals — a fixed number of hands, two, three or six. Lowest total at the end wins.',
  'rules.formats.pool':
    'Pool — everybody keeps a running total and you are out when you cross 101 or 201. The last player standing wins. In 201 a player who crosses can buy back in once, resuming at the highest score still in play.',

  'rules.tips.title': 'Four things that will save you points',
  'rules.tips.pure':
    'Build the pure sequence first. Without one, the whole hand counts against you, however tidy the rest of it looks.',
  'rules.tips.arrange':
    'Keep your cards in their groups as you go. You are scored on the arrangement you actually made, not on the best one you could have made, and the table shows you what it is currently worth.',
  'rules.tips.drop':
    'A bad hand is worth dropping. Twenty points on the first turn is cheaper than eighty at the end.',
  'rules.tips.wild':
    'Hold your wilds. They cost nothing if you lose the hand and they can finish a run you would never otherwise complete.',

  'rules.play': 'Got it — let me play',
  'card.index.K': 'K',
  'rules.full': 'Read the full rules \u203a',
  'rules.new': 'New to Rummy? Read how to play \u203a',
} as const;

export type MessageKey = keyof typeof en;
export type Catalogue = Partial<Record<MessageKey, string>>;
